import { Box, Checkbox, TextField, Typography, createFilterOptions } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import type { ReactElement, ReactNode } from 'react';

import { hasValue } from '@core/utils/equality';

import { InputTooltip } from '../InputTooltip/InputTooltip';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { LoadingOption } from '../LookupInput/ApiLookupInputBase';

export type InputMultiLookupOption<T> = {
  value: T;
  label: string;
  sublabels?: string[];
  isDisabled?: boolean;
};

// Typing in the autocomplete box will match against the label
const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option: InputMultiLookupOption<any>) => option.label,
});

type InputMultiLookupBaseProps<OptionValueType> = {
  label?: string;
  name?: string;
  options: InputMultiLookupOption<OptionValueType>[];
  value: InputMultiLookupOption<OptionValueType>[];
  onChange: (selectedOptions: InputMultiLookupOption<OptionValueType>[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  /** Helper text to display below the input */
  helperText?: string;
  /** Adding a tip will wrap the input component in a hoverable tooltip */
  tip?: string;
  /** if true - the options will be displayed with checkbox */
  withCheckbox?: boolean;
  /** custom render function for the value of the input. */
  renderValue?: (selected: InputMultiLookupOption<OptionValueType>[]) => ReactNode;
  /**
   * Callback fired whenever the user types in the input field.
   * Combine with `inputValue` to control the text and trigger server-side searches. */
  onInputValueChange?: (value: string) => void;
  /**
   * Controlled text value shown in the autocomplete input.
   * Use with `onInputChange` for a fully controlled typing experience. */
  inputValue?: string;
  /**
   * Use if options are filtered somewhere else, like during API requests.
   * Combine with onInputValueChange and inputValue to get the filter string.
   */
  disableClientSideFiltering?: boolean;
};

export const InputMultiLookupBase = <OptionValueType,>({
  label,
  name,
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  loading,
  error,
  helperText,
  tip,
  withCheckbox,
  renderValue,
  onInputValueChange,
  inputValue,
  disableClientSideFiltering,
}: InputMultiLookupBaseProps<OptionValueType>): ReactElement => {
  const hasError = error === true;

  const labelWithTip =
    label === undefined ? null : (
      <Box display='flex' gap='0.25rem' alignItems='center'>
        {label}
        {tip !== undefined ? <InputTooltip tip={tip} /> : null}
      </Box>
    );

  return (
    <Autocomplete
      disableCloseOnSelect={withCheckbox === true}
      multiple
      id={name}
      onBlur={onBlur}
      disabled={disabled}
      loading={loading}
      loadingText={LoadingOption.label}
      isOptionEqualToValue={(option, val): boolean =>
        val === undefined || option.value === val.value
      }
      value={value}
      inputValue={inputValue}
      onInputChange={(_: unknown, newInputValue) => {
        onInputValueChange?.(newInputValue);
      }}
      freeSolo
      forcePopupIcon
      onChange={(
        _: unknown,
        selectedOpts: (string | InputMultiLookupOption<OptionValueType>)[],
      ): void => {
        onChange(selectedOpts.filter((opt) => typeof opt !== 'string'));
      }}
      autoComplete={false}
      disablePortal
      fullWidth
      options={loading === true ? [] : options}
      renderTags={renderValue}
      renderInput={(params): ReactElement => (
        <TextField
          {...params}
          name={name}
          error={hasError}
          helperText={helperText}
          placeholder={placeholder}
          label={labelWithTip}
          // Close outlined box + fix height if label is undefined
          sx={{
            ...(label === undefined && {
              fieldset: { top: 0 },
              legend: { display: 'none' },
            }),
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading === true ? (
                  <LoadingSpinner color='inherit' size={20} wrapInBox={false} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      filterOptions={
        /* undefined doesn't work here, because it uses default filter options,
          and we need strictly x => x to allow text "N more options" */
        disableClientSideFiltering
          ? (a): InputMultiLookupOption<OptionValueType>[] => a
          : filterOptions
      }
      getOptionDisabled={(option) => option.isDisabled === true}
      renderOption={(params, option, { selected }): ReactElement => (
        <Box {...params} key={`${name}-options-${option.value}`} component='li'>
          {withCheckbox === true && <Checkbox style={{ marginRight: 8 }} checked={selected} />}
          <Box
            display='flex'
            flexDirection='column'
            alignItems='start !important'
            padding='0rem'
            whiteSpace='nowrap'
          >
            <Typography>{option.label}</Typography>

            {hasValue(option.sublabels) && option.sublabels.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {option.sublabels
                  .filter((sl) => sl !== '')
                  .map((sublabel, subLabelIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Typography key={`${sublabel}-${subLabelIndex}`} variant='body3'>
                      {sublabel}
                    </Typography>
                  ))}
              </Box>
            )}
          </Box>
        </Box>
      )}
    />
  );
};
